import React, { useEffect, useState } from 'react';
import { FileText, Plus, Download, Eye, Shield, X, Award, FileCheck, FilePlus, Trash2, Lock, ThumbsUp, Printer } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { fetchPlatformSettings } from '../../services/platformSettings';
import {
  buildOfficialDocumentHtml,
  downloadOfficialDocumentHtml,
  printOfficialDocumentHtml,
} from '../../utils/officialDocumentHtml';
import './AdminPages.css';

const DOC_TYPES = [
  { id: 'invoice', label: 'Invoice', icon: FileText, color: 'var(--success)' },
  { id: 'quotation', label: 'Quotation', icon: FilePlus, color: 'var(--secondary-color)' },
  { id: 'contract', label: 'Employment Contract', icon: FileCheck, color: 'var(--info)' },
  { id: 'internship', label: 'Internship Contract', icon: FileCheck, color: 'var(--warning)' },
  { id: 'certificate', label: 'Certificate', icon: Award, color: 'var(--accent-light)' },
];

const STATUS_BADGE = { Draft: 'badge-gray', 'Pending Approval': 'badge-warning', Approved: 'badge-success', Signed: 'badge-success', Issued: 'badge-info' };

function approvalList(doc) {
  return doc.approvedByUids || [];
}

export default function DocumentsPage() {
  const { data, commitData, showToast } = useApp();
  const { can, isFounder, user } = useAuth();
  const canGenerate = can('canGenerateDoc');
  const docs = data.documents || [];

  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [form, setForm] = useState({ recipient: '', recipientEmployeeId: '', amount: '', description: '', date: '' });
  const [branding, setBranding] = useState({ companyName: '', tagline: '' });
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchPlatformSettings()
      .then((s) => {
        if (!cancelled && s?.branding) {
          setBranding({
            companyName: s.branding.companyName || '',
            tagline: s.branding.tagline || '',
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!canGenerate) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Documents</h1>
          </div>
        </div>
        <div className="perm-denied">
          <div className="perm-denied-icon">
            <Lock size={26} style={{ color: 'var(--text-dim)' }} />
          </div>
          <p style={{ fontWeight: 700 }}>Access Restricted</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Document generation is available to Finance, HR, and Founders.
          </p>
        </div>
      </div>
    );
  }

  const generate = async () => {
    if (!selectedType || !form.recipient) {
      showToast('Select type and enter recipient', 'error');
      return;
    }
    const label = DOC_TYPES.find((d) => d.id === selectedType)?.label || selectedType;
    const id = String(Date.now());
    try {
      await commitData('documents', [
        ...docs,
        {
          id,
          type: label,
          name: `${label} — ${form.recipient}`,
          recipient: form.recipient,
          recipientEmployeeId: form.recipientEmployeeId || '',
          date: form.date || new Date().toISOString().split('T')[0],
          status: 'Pending Approval',
          approvals: 0,
          approvedByUids: [],
          description: form.description || '',
          amount: form.amount ? parseFloat(form.amount) : 0,
          createdBy: user?.employeeId != null ? String(user.employeeId) : '',
          createdByUid: user?.uid || '',
          createdByName: user?.name || 'Staff',
        },
      ]);
      showToast(`${label} submitted — founders notified by email when mail is configured`);
      setShowModal(false);
      setForm({ recipient: '', recipientEmployeeId: '', amount: '', description: '', date: '' });
      setSelectedType('');
    } catch {
      /* commitData shows toast */
    }
  };

  const approveDoc = async (doc) => {
    const list = approvalList(doc);
    if (list.includes(user.uid)) {
      showToast('You have already approved this document', 'warning');
      return;
    }
    const newList = [...list, user.uid];
    const newStatus = newList.length >= 2 ? 'Approved' : 'Pending Approval';
    try {
      await commitData(
        'documents',
        docs.map((d) =>
          d.id === doc.id ? { ...d, approvedByUids: newList, approvals: newList.length, status: newStatus } : d
        )
      );
      showToast(
        newList.length >= 2
          ? 'Document fully approved — requester emailed when mail is configured'
          : 'Your approval recorded — co-founder notified'
      );
    } catch {
      /* */
    }
  };

  const issueDoc = async (doc) => {
    if (doc.status !== 'Approved') {
      showToast('Both founders must approve first', 'error');
      return;
    }
    try {
      await commitData('documents', docs.map((d) => (d.id === doc.id ? { ...d, status: 'Issued' } : d)));
      showToast('Document issued — recipient notified when mail is configured');
    } catch {
      /* */
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await commitData(
        'documents',
        docs.filter((d) => d.id !== id)
      );
      showToast('Document deleted', 'warning');
    } catch {
      /* */
    }
  };

  const openPreview = (doc) => setPreviewDoc(doc);

  const handleDownload = (doc) => {
    try {
      const base = (doc.name || 'document').replace(/[^\w\-]+/g, '_').slice(0, 80);
      downloadOfficialDocumentHtml(doc, branding, base);
      showToast('Styled HTML document downloaded — open in a browser or Word; use Print → Save as PDF if needed.');
    } catch (e) {
      showToast(e.message || 'Download failed', 'error');
    }
  };

  const handlePrint = (doc) => {
    try {
      printOfficialDocumentHtml(doc, branding);
    } catch (e) {
      showToast(e.message || 'Print failed', 'error');
    }
  };

  const previewHtml = previewDoc ? buildOfficialDocumentHtml(previewDoc, branding) : '';

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Documents</h1>
          <p className="page-subtitle">Generate and manage official company documents</p>
        </div>
        <button className="btn-gold" type="button" onClick={() => setShowModal(true)}>
          <Plus size={15} /> Generate Document
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 12, marginBottom: 24 }}>
        {DOC_TYPES.map((dt) => (
          <button
            type="button"
            key={dt.id}
            className="card"
            style={{ cursor: 'pointer', textAlign: 'center', padding: 18, border: '1px solid var(--border-color)', background: 'var(--surface-color)' }}
            onClick={() => {
              setSelectedType(dt.id);
              setShowModal(true);
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 'var(--radius-md)',
                background: `${dt.color}15`,
                color: dt.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 10px',
              }}
            >
              <dt.icon size={20} />
            </div>
            <p style={{ fontSize: '0.82rem', fontWeight: 700 }}>{dt.label}</p>
            <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 3 }}>Click to generate</p>
          </button>
        ))}
      </div>

      <div className="info-banner info-banner-gold" style={{ marginBottom: 20 }}>
        <Shield size={15} />
        <strong>Dual-Founder Approval</strong>
        <span style={{ fontWeight: 400 }}> — All official documents require sign-off from both founders before issuance.</span>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Document</th>
              <th>Type</th>
              <th>Date</th>
              <th>Approvals</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((doc) => {
              const list = approvalList(doc);
              const myApproval = list.includes(user.uid);
              return (
                <tr key={doc.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <FileText size={15} style={{ color: 'var(--secondary-color)' }} />
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{doc.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-gray">{doc.type}</span>
                  </td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{new Date(doc.date).toLocaleDateString('en-ZA')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {[0, 1].map((i) => (
                        <div key={i} className={`approval-dot ${i < (doc.approvals || 0) ? 'filled' : 'empty'}`}>
                          {i < (doc.approvals || 0) ? '✓' : i + 1}
                        </div>
                      ))}
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginLeft: 4 }}>{doc.approvals || 0}/2</span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[doc.status] || 'badge-gray'}`}>{doc.status}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn-ghost"
                        style={{ padding: '5px 8px', fontSize: '0.72rem' }}
                        title="Preview"
                        onClick={() => openPreview(doc)}
                      >
                        <Eye size={12} />
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        style={{ padding: '5px 8px', fontSize: '0.72rem' }}
                        title="Download HTML"
                        onClick={() => handleDownload(doc)}
                      >
                        <Download size={12} />
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        style={{ padding: '5px 8px', fontSize: '0.72rem' }}
                        title="Print / Save as PDF"
                        onClick={() => handlePrint(doc)}
                      >
                        <Printer size={12} />
                      </button>
                      {isFounder && doc.status === 'Pending Approval' && !myApproval && (
                        <button
                          type="button"
                          className="btn-gold"
                          style={{ padding: '5px 8px', fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={() => approveDoc(doc)}
                        >
                          <ThumbsUp size={11} /> Approve
                        </button>
                      )}
                      {isFounder && doc.status === 'Approved' && (
                        <button type="button" className="btn-outline" style={{ padding: '5px 8px', fontSize: '0.72rem' }} onClick={() => issueDoc(doc)}>
                          Issue
                        </button>
                      )}
                      {isFounder && (
                        <button
                          type="button"
                          className="btn-ghost"
                          style={{ padding: '5px 8px', fontSize: '0.72rem', color: 'var(--danger)' }}
                          onClick={() => deleteDoc(doc.id)}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)} role="presentation">
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <p className="modal-title">Generate Document</p>
              <button type="button" className="modal-close" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label className="form-label">Document Type *</label>
                <select className="form-input" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                  <option value="">Select type...</option>
                  {DOC_TYPES.map((dt) => (
                    <option key={dt.id} value={dt.id}>
                      {dt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Recipient name *</label>
                <input
                  className="form-input"
                  placeholder="Name or company"
                  value={form.recipient}
                  onChange={(e) => setForm({ ...form, recipient: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Link to employee (for email)</label>
                <select className="form-input" value={form.recipientEmployeeId} onChange={(e) => setForm({ ...form, recipientEmployeeId: e.target.value })}>
                  <option value="">None</option>
                  {data.employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
              {(selectedType === 'invoice' || selectedType === 'quotation') && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Amount (R)</label>
                    <input className="form-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Issue Date</label>
                    <input className="form-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Notes / Description</label>
                <textarea className="form-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Additional details..." />
              </div>
              <div className="info-banner info-banner-gold">
                <Shield size={13} />
                <span>Requires dual-founder approval before issuance.</span>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn-gold" onClick={generate}>
                Generate &amp; Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {previewDoc && (
        <div className="modal-overlay" onClick={() => setPreviewDoc(null)} role="presentation">
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            style={{ maxWidth: 920, width: '96%', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header">
              <p className="modal-title">Document preview</p>
              <button type="button" className="modal-close" onClick={() => setPreviewDoc(null)}>
                <X size={18} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '0 16px 12px', flexWrap: 'wrap' }}>
              <button type="button" className="btn-gold" style={{ fontSize: '0.8rem' }} onClick={() => handleDownload(previewDoc)}>
                <Download size={14} style={{ marginRight: 6 }} />
                Download HTML
              </button>
              <button type="button" className="btn-outline" style={{ fontSize: '0.8rem' }} onClick={() => handlePrint(previewDoc)}>
                <Printer size={14} style={{ marginRight: 6 }} />
                Print / PDF
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: '0 16px 16px' }}>
              <iframe title="Document preview" srcDoc={previewHtml} style={{ width: '100%', height: 'min(70vh, 640px)', border: '1px solid var(--border-color)', borderRadius: 8, background: '#fff' }} sandbox="allow-same-origin" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
